/**
 * Разметка ответов чата: показ Markdown и ВНУТРЕННИЕ ССЫЛКИ (PROP-386).
 *
 * ЗАЧЕМ ОБЩИЙ ФАЙЛ. Ответы рисуют двое — страница чата и панель агента, — и оба через `v-html`.
 * Правило «что считать ссылкой» и правило «что вырезать» обязаны быть одни: разъехавшись, они
 * дадут не разную картинку, а разную защиту, и заметить это можно будет только по последствиям.
 *
 * ЧТО ЗДЕСЬ ГЛАВНОЕ. Текст ответа сочиняет модель по данным, пришедшим снаружи, и попадает он
 * в `v-html`, то есть в разметку страницы как есть. `marked` пропускает СЫРОЙ HTML насквозь —
 * это проверялось в PROP-369 на самом рендерере. Пока ссылок не было, поверхность держалась на
 * том, что модель их не писала; приглашая её ставить ссылки, полагаться на это нельзя.
 *
 * Поэтому итоговая разметка проходит через разбор и чистку: остаются ссылки и обычное
 * форматирование, исчезают скрипты, обработчики событий и схемы вроде `javascript:`.
 */
import { marked } from 'marked'

/** Схемы, которые вообще имеют смысл в ответе. Всё прочее — не ссылка, а способ выполнить код. */
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:']

/** Внутренний адрес: свой путь платформы. `//host` — уже чужой сайт, поэтому исключён явно. */
export function isInternalHref(href) {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//')
}

/**
 * Обезвредить разметку: сохранить смысл, убрать исполнение.
 *
 * Разбор через `DOMParser`, а не регулярками: разметку правит браузер тем же кодом, что потом
 * её и показывает, поэтому пропущенных форм не остаётся — а регулярка по HTML их всегда
 * оставляет.
 */
export function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html')

  doc.querySelectorAll('script, style, iframe, object, embed, form, input, button').forEach(el => el.remove())

  doc.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => {
      const name = attr.name.toLowerCase()
      // Любой `on*` — это обработчик события, то есть код в разметке.
      if (name.startsWith('on') || name === 'srcdoc') el.removeAttribute(attr.name)
    })
  })

  doc.querySelectorAll('a[href]').forEach(a => {
    const href = a.getAttribute('href') || ''
    if (isInternalHref(href)) {
      // Признак для обработчика переходов: по нему клик уводит роутером, а не перезагрузкой.
      a.setAttribute('data-cbs-internal', '1')
      return
    }
    let url = null
    try {
      url = new URL(href, window.location.origin)
    } catch (e) {
      url = null
    }
    if (!url || !SAFE_SCHEMES.includes(url.protocol)) {
      // Не ссылка — оставляем текст. Убрать вместе с текстом значило бы потерять содержание
      // ответа из-за формы, в которую его завернули.
      a.removeAttribute('href')
      return
    }
    a.setAttribute('target', '_blank')
    // Без `noopener` открытая вкладка получает доступ к нашей через `window.opener`.
    a.setAttribute('rel', 'noopener noreferrer')
  })

  return doc.body.innerHTML
}

/** Markdown → безопасная разметка. Хвостовой перевод строки снят: он лишний в строке с временем. */
export function renderChatMarkdown(text) {
  if (!text) return ''
  return sanitizeHtml(marked.parse(String(text)).trim())
}

/**
 * Перехват клика по внутренней ссылке.
 *
 * Вешается ОДИН на всю ленту, а не на каждую ссылку: сообщения приходят потоком, и навешивать
 * обработчики на разметку из `v-html` пришлось бы после каждого ответа.
 *
 * @returns {boolean} true — переход выполнен роутером, событие можно считать обработанным
 */
/**
 * BUG-439: есть ли такой маршрут на самом деле.
 *
 * У ненайденного пути совпадает только catch-all «*», который уводит на 404, — поэтому мало
 * проверить, что что-то совпало.
 */
export function routeExists(router, href) {
  try {
    const route = router.resolve(href).route || {}
    const matched = route.matched || []
    if (!matched.length) return false
    // ⚠️ Проверять одно лишь «что-то совпало» НЕДОСТАТОЧНО, и проверять `*` — тоже: catch-all
    // объявлен через `redirect`, а `resolve` редирект уже выполняет и возвращает готовую
    // страницу 404. То есть у выдуманного пути совпадение есть, и оно выглядит настоящим.
    // Замерено на живом маршрутизаторе: `/cubus/object` → matched[0].path === '/error-404'.
    if (route.name === 'error-404') return false
    return matched[0].path !== '*' && matched[0].path !== '/error-404'
  } catch (e) {
    return false
  }
}

export function handleInternalClick(event, router, onDeadLink) {
  const a = event.target && event.target.closest ? event.target.closest('a[href]') : null
  if (!a) return false
  const href = a.getAttribute('href')
  if (!isInternalHref(href)) return false
  event.preventDefault()
  // Переход на ТЕКУЩИЙ адрес роутер отвергает исключением NavigationDuplicated — для человека
  // это «ссылка не работает», хотя он уже на месте.
  if (router.currentRoute.fullPath === href) return true
  // BUG-439: адрес в ответе сочиняет модель, и он может не соответствовать ни одному маршруту.
  // Тогда переход означал бы 404 — со стороны это поломка платформы, а не выдуманная ссылка.
  if (!routeExists(router, href)) {
    // Молча проглотить клик — тоже враньё: человек решит, что ссылка «не нажимается».
    if (typeof onDeadLink === 'function') onDeadLink(href)
    return true
  }
  router.push(href).catch(() => { /* переход отменён гардом или дублирует текущий */ })
  return true
}

/**
 * Внутренние ссылки ответа, без повторов (PROP-400).
 *
 * Рендерим ответ ТЕМ ЖЕ путём, что и показ, и берём только якоря, помеченные `data-cbs-internal`,
 * — ровно те, что клик уводит роутером. Один и тот же адрес, встреченный дважды, — это один
 * вариант, поэтому список без дублей: «ровно один вариант» не должно ломаться от повтора ссылки.
 */
export function internalLinksOf(text) {
  if (!text) return []
  const doc = new DOMParser().parseFromString(renderChatMarkdown(text), 'text/html')
  const hrefs = []
  doc.querySelectorAll('a[data-cbs-internal]').forEach(a => {
    const href = a.getAttribute('href') || ''
    if (isInternalHref(href) && !hrefs.includes(href)) hrefs.push(href)
  })
  return hrefs
}

/**
 * Есть ли в запросе явный ЗАПРЕТ открывать ссылку (PROP-401).
 *
 * Правило авто-открытия перевёрнуто: единственную внутреннюю ссылку ответа открываем ПО
 * УМОЛЧАНИЮ, и только явное «не открывай / не переходи / без открытия» это отменяет. Признак —
 * отрицание «не» перед глаголом открытия/перехода (допускаем «не надо/нужно/стоит открывать»)
 * либо оборот «без открытия». Граница слева по «не-букве» (в JS `\b` не знает кириллицы).
 */
const NO_OPEN_INTENT = /(?:^|[^а-яёa-z])не\s+(?:надо\s+|нужно\s+|стоит\s+|обязательно\s+)?(?:открыв|откро|перейд|перех)|без\s+открыт/i
export function saysDoNotOpen(text) {
  return typeof text === 'string' && NO_OPEN_INTENT.test(text)
}

/**
 * Просят ли ОТКРЫТЬ объект (PROP-407).
 *
 * Нужен режиму презентации: на «открой Валюты» страница не нужна — нужен сам объект, и клиент
 * откроет его по единственной внутренней ссылке (`PROP-401`). Страницу просим у агента во всех
 * ОСТАЛЬНЫХ случаях. Признак — повелительный глагол открытия/перехода; отрицание («не открывай»)
 * сюда не попадает, его отдельно ловит `saysDoNotOpen`.
 */
const OPEN_INTENT = /(?:^|[^а-яёa-z])(открой(?:те)?|открыть|перейд(?:и|ите|ти)|зайд(?:и|ите))/i
export function wantsOpenObject(text) {
  return typeof text === 'string' && OPEN_INTENT.test(text) && !saysDoNotOpen(text)
}
